// Shared "Labor resources" chip template: a neutral pill holding a dark
// avatar (the acronym) and the units, with a remove control shown on hover.
// With the portfolio's units option off, the chip is the dark acronym bubble
// alone (the "--compact" classes).
// Used by the resourceassignment column of the main project Gantt and the
// Scenario Gantt (spec D5), which share this column config; by the Program and
// Multi-Project Gantts, whose rows carry the same loadBadges / loadStale as the
// scenario feed, so their chips take the same load colour, display only (no
// remove, no units editor; the "+" only on a row editing allows); and, display
// only with no load colour, by the Task Focus Gantt (collaborator chips, rows
// with no loadBadges). The display rule and test hooks live in one place.
//
// Does NOT import from "./utils": utils.js imports { NonLaborResourceColumn,
// processDefaultColumns } from "./config", so importing utils.js here (while
// config.js imports this module) would create a load-order cycle. getInitials
// is instead re-implemented locally as initialsOf, mirroring utils.js's
// getInitials.
import i18n from "../../../i18n";
import { escapeHtml } from "../../../utils/escapeHtml";
import { resourceChipAcronymHtml, resourceChipBodyHtml } from "./resourceChip/resourceChipHtml";
import { CHIP_DIMENSIONS, resourceChipModel } from "./resourceChip/resourceChipModel";
import { chipT, loadStateFor } from "./resourceChip/loadStateFor";
import { scheduleLaborChipFit } from "./resourceChip/laborChipFit";

// Portfolio option (Settings > Gantt options): show each resource's units next
// to its acronym and let a click on the chip edit them. Fed from the portfolio
// details like the display-unit cache; off until the details say otherwise,
// which is the option's default.
let laborUnitsEnabled = false;

const laborUnitsListeners = new Set();

export const setLaborUnitsEnabled = (details) => {
  const next = details?.laborUnitsEnabled === true;
  if (next === laborUnitsEnabled) return;
  laborUnitsEnabled = next;
  laborUnitsListeners.forEach((l) => l(laborUnitsEnabled));
};

// ganttChipOptions merges this into its own subscription (it cannot be imported
// here at module level without a cycle).
export const onLaborUnitsEnabledChange = (listener) => {
  laborUnitsListeners.add(listener);
  return () => laborUnitsListeners.delete(listener);
};

export const isLaborUnitsEnabled = () => laborUnitsEnabled;

const initialsOf = (name) => {
  if (!name) return "";
  const words = name.split(" ");
  const firstLetter = words[0][0];
  const secondLetter = words.length > 1 ? words.slice(-1)[0][0] : "";
  return (firstLetter + secondLetter).toUpperCase();
};

export const formatUnits = (units) => {
  const n = Number(units);
  if (!Number.isFinite(n)) return "0%";
  return `${Number.isInteger(n) ? n : Number(n.toFixed(2))}%`;
};

// Bryntum's assignment editor rounds units to an integer — its tags and the
// picker's Units column alike — while the chip shows two places: the same
// assignment read 33% on double-click and 33.33% on the chip. These give the
// editor the chip's precision.
export const assignmentFieldChipView = {
  cls: "b-assignment-chipview",
  itemTpl: (assignment) => `${escapeHtml(assignment.resourceName ?? "")} ${formatUnits(assignment.units)}`,
  scrollable: { overflowX: "hidden-scroll" },
};

export const applyPickerUnitsPrecision = (picker) => {
  const units = picker?.columns?.get?.("units");
  if (!units) return;
  units.renderer = ({ value }) => (value ? formatUnits(value) : "");
  // Read by the cell editor NumberField, built on the first edit.
  units.format = "9.##";
};

const LABOR_ENTITY = "TEAMS";
const TEAM_CHIPS = new Set([LABOR_ENTITY]);
// Task Focus assigns collaborators (the people of a team), not teams.
const TASK_FOCUS_CHIPS = new Set([LABOR_ENTITY, "COLLABORATOR"]);

const acronymOf = (resource) => resource.acronym || initialsOf(resource.name);

// The column fits two pills; a third wraps out of sight. The cell tooltip lists
// every labor couple so none is unreachable. HTML (escaped): Bryntum renders
// tooltip output as markup.
export const laborCouplesTooltip = (assignments) =>
  (assignments || [])
    .filter((a) => a?.resource?.entityType === LABOR_ENTITY)
    .map((a) => `${escapeHtml(acronymOf(a.resource))} ${formatUnits(a.units)}`)
    .join(" · ");

// Interpolated once per (language, resource name): the template runs for every
// rendered chip on every repaint.
const removeLabels = new Map();
const removeLabelFor = (name) => {
  const k = `${i18n.language}|${name}`;
  let label = removeLabels.get(k);
  if (label === undefined) {
    label = escapeHtml(i18n.t("ganttContext.unitsInlineEdit.remove", { name }));
    if (removeLabels.size > 5000) removeLabels.clear();
    removeLabels.set(k, label);
  }
  return label;
};

// The remove control carries no text, so the chip's textContent stays
// "<ACRONYM><units>%" (just "<ACRONYM>" with the units option off) for whoever
// reads it (e2e helpers, the export).
export const laborChipHtml = (assignment, load = null, entityTypes = TEAM_CHIPS) => {
  const { resource, units, event, eventId } = assignment;
  if (!resource || !entityTypes.has(resource.entityType)) return "";

  const initials = escapeHtml(acronymOf(resource));
  const taskId = event?.id ?? eventId ?? "";
  // The units stay in the tooltip even when the chip shows them: the "+N" of
  // a row too narrow for every chip lists its hidden chips from these tooltips.
  const chipTip = escapeHtml(
    [`${resource.name} · ${formatUnits(units)}`, load?.tooltip]
      .filter(Boolean)
      .join(" · ")
  );
  const removeLabel = removeLabelFor(resource.name);
  const removeHtml = `<span class="offolio-labor-chip__remove" role="button" aria-label="${removeLabel}" data-btip="${removeLabel}" data-testid="gantt-labor-chip-remove-${taskId}-${resource.id}"></span>`;
  const acronymTestId = `gantt-labor-chip-acronym-${taskId}-${resource.id}`;
  const attrs = `data-testid="gantt-labor-chip-${taskId}-${resource.id}"
         data-resource-id="${resource.id}"
         data-btip="${chipTip}"`;

  // Every option off (no units, no load colour): the dark acronym bubble the
  // column always had, untouched.
  if (!laborUnitsEnabled && !load) {
    return `
    <div class="offolio-labor-chip offolio-labor-chip--compact"
         ${attrs}>
      <span class="offolio-labor-chip__acronym" data-testid="${acronymTestId}">${initials}</span>${removeHtml}
    </div>`;
  }

  // Otherwise the shared chip anatomy (resourceChipHtml.js): the acronym
  // segment, coloured by the load, then the units alone in the light body.
  const model = resourceChipModel({
    dimension: CHIP_DIMENSIONS.LABOR,
    taskId,
    resourceId: resource.id,
    name: resource.name,
    acronym: acronymOf(resource),
    valueText: laborUnitsEnabled ? formatUnits(units) : "",
    load,
  });
  const unitsHtml = laborUnitsEnabled
    ? `<span class="offolio-labor-chip__units offolio-resource-chip__value" data-testid="gantt-labor-chip-units-${taskId}-${resource.id}">${formatUnits(units)}</span>`
    : "";

  return `
    <div class="offolio-labor-chip ${model.rootClass}"
         ${attrs}>
      ${resourceChipAcronymHtml(model, { cls: "offolio-labor-chip__acronym", testId: acronymTestId })}${resourceChipBodyHtml(unitsHtml)}${removeHtml}
    </div>`;
};

export const laborChipTpl = (assignment) =>
  laborChipHtml(
    assignment,
    assignment?.resource
      ? loadStateFor(assignment.event, assignment.resource.id, assignment.resource.name, chipT, CHIP_DIMENSIONS.LABOR)
      : null
  );

// Task Focus rows are tasks, not schedule activities: no load to colour by.
export const taskFocusChipTpl = (assignment) => laborChipHtml(assignment, null, TASK_FOCUS_CHIPS);

// The dashed "+" closing the chip list: opens the assignment picker.
export const laborAddButtonHtml = (taskId) => {
  const label = escapeHtml(i18n.t("ganttContext.unitsInlineEdit.add"));
  return `<span class="offolio-labor-add" role="button" aria-label="${label}" data-btip="${label}" data-testid="gantt-labor-add-${taskId}"></span>`;
};

// Runs after Bryntum's own chip-view renderer (as the column's `renderer`, it
// is Bryntum's "external" one) and finishes the cell: flags the chip list for
// styling, hides the remove control on a lone chip (a task keeps at least one
// labor resource: the picker's save refuses an empty selection too) and on a
// row the user cannot edit, and ends an editable row's list with the "+".
export const decorateLaborCell = ({ cellElement, record, isExport }, { canAdd } = {}) => {
  if (isExport || !cellElement) return;
  const chipView = cellElement.querySelector(".b-assignment-chipview");
  if (!chipView) return;
  const laborCount = (record?.assignments || []).filter(
    (a) => a?.resource?.entityType === LABOR_ENTITY
  ).length;
  const editable = !!record && !!canAdd?.(record);
  chipView.classList.add("offolio-labor-chipview");
  chipView.classList.toggle("offolio-labor-chipview--single", laborCount <= 1);
  chipView.classList.toggle("offolio-labor-chipview--readonly", !editable);
  // Beside the chip view, not inside it: the chip view clips what does not
  // fit on the line, and the "+" must never be clipped nor cover a chip.
  // Bryntum empties the wrap on every render, so there is no stale "+".
  if (editable) chipView.insertAdjacentHTML("afterend", laborAddButtonHtml(record.id));
  // Chip anatomy (units or load colour on): whole chips only, in full, the
  // rest counted behind "+N" when the cell is too narrow (laborChipFit.js). The
  // compact bubbles (every option off) keep the column's historical clipping.
  const anatomy = !!chipView.querySelector(".offolio-resource-chip");
  chipView.classList.toggle("offolio-labor-chipview--chips", anatomy);
  if (anatomy) scheduleLaborChipFit(cellElement);
};
